import * as _ from 'lodash';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { switchMap, map, filter } from 'rxjs/operators';
import { GraphApiService } from '@nuso/common/data-access-api';
import { Customer } from '@nuso/orgs/types';
import { Property } from '@nuso/common/util-object';

import { fromRoot } from '@nuso/portal/state';
import { Service, ServiceOrderLineItem } from '@nuso/portal/util-services';
import { isBlueServiceType, isGreenServiceType } from '@nuso/services/types';
import { FetchPolicy } from '@apollo/client';
import { normalizePlatformServices, platformServiceFields } from '@nuso/services/data-access';
import { PlatformService } from '@nuso/services/types';
import { BridgeForZoomSettings } from '@nuso/bridge/zoom/types';
import { gql } from 'graphql-tag';

export type ObservableCustomer = Observable<Customer>;

export interface ServiceOrderDetails {
  id: string;
  customerId: number;
  accountNumber: string;
  customerName: string;
  teamworkCustomerId: number;
  regionCode?: string;
  type: string;
  changeServiceId: string;
  state: string;
  status: string;
  statusDescription: string;
  createdAt: string;

  lineItems: ServiceOrderLineItem[]
  configuration?: Property[];

  hardwareShippingAddress?: any;

  services: Service[];
  customer: any;
  inboundApiOrders?: any[];
}

export interface PbxVerification {
  customerId: number;
  customerName: string;
  customerAccountNumber: string;
  pbxNumber: string;
  pbxInfo: {
    existsInMeta: boolean;
  }
}

export const mainCustomerFields = `
  id
  name
  displayName
  accountNumber
  displayAccountNumber
  customId
  accountId
  billingAccountNumber
  reportingAccountNumber
  regionCode
  region
  timezone
  status
  mainServiceKey
  salesforceId
  nonBillableSalesforceId
  revioId
  pbxNumber
  internationalCallBlocked
  regionRedirectUrl
  billableAccountNumber
  billableAccountName
  isBillable
  isMainAccount
  billableAccountType
  isResellerMainAccount
  canBeManagedByResellerUser
  defaultTrunkIdForGreenService
  defaultServerForBridgeForTeamsService
  messagingCapable
  smsFeatureEnabled
  mmsFeatureEnabled
  type
  languageCode
  serviceCountry
`;

export const taskFields = `
  id
  key
  description
  actor
  source
  path
  state
  status
  statusDescription
  updatedAt
  data {
    key
    value
  }
  lastError {
    code
    message
  }
`;

export const milestoneFields = `
  id
  serviceId
  key
  description
  state
  tasks {
    ${taskFields}
  }
`;


export const mainServiceFields = `
  id
  type
  name
  productCode
  state
  orderId
  isProvisioned

  configuration {
    key
    value
  }

  usage {
    setting
    max
    used
  }
`;

export const mainMonitorFields = `
  id
  ipAddress
  deviceType
  nodeId
  status
  settingsKey
`;

export const mainBusinessGroupFields = `
  accountNumber
  localCNAMName
  profile
  directoryNumberCount
  freeDirectoryNumberCount
  managedDeviceCount

  easSystem {
    id
    wsdVersion
  }
`;

export const greenSettingsFields = `
  id
  domain {
    id
    name
    territory
    description
  }
`;

export const bridgeForTeamsFields = `
  id
  enterprise {
    id
    tenantId
    name
    resellerId

    maxSeats
    domainAdmin
  }
  teamsEnabledUsers {
    id
    name
    teamsUser
    authUserName
    did
    pbxRegistrationStatus
    pbxLastRegistrationAt
    teamsRegistrationStatus
    teamsLicense
    phoneSystemAssigned
    phoneSystemProvisioned

    platformUser {
      id
      did
      name
    }
  }
  availableTeamsUsers {
    email
  }
  availablePlatformUsers {
    id
    did
    name
    platform
  }
`;

export const teamsOcSettingsFields = `
  customerId
  tenantId
  contactEmail
  contactName
`;

export const bridgeTeamsAuthEmailSettingsFields = `
  customerId
  tenantId
  contactEmail
`;

export const customerByIdQuery = gql`
  query customerById($id: Int!) {
    customerById(id: $id) {
      ${mainCustomerFields}
      hasBusinessGroup
      descendants {
        ${mainCustomerFields}
        monitors {
          ${mainMonitorFields}
        }
      }
      parents {
        id
        name
      }
      partner {
        id
        name
        isNecPartner
      }
      platformServices {
        ${platformServiceFields}
      }
      hasActiveRecordingService

      features {
        id
        featureKey
        customerId
        status
      }

      greenSettings {
        ${greenSettingsFields}
      }

      isResellerMainAccount
      billableAccountType
    }
  }
`;

const updateServiceConfigurationMutation = gql`
  mutation updateServiceConfiguration($input: UpdateServiceConfigurationInput!) {
    updateServiceConfiguration(input: $input) {
      service {
        id
        state

        configuration {
          key
          value
        }
      }
      milestone {
        id
        state
      }
      task {
        id
        state
        status
        statusDescription
        updatedAt
      }
    }
  }
`;

const getServiceQuery = gql`
  query getService($serviceId: ID!) {
    getService(serviceId: $serviceId) {
      ${mainServiceFields}
      milestones {
        id
        state
        tasks {
          ${taskFields}
        }
      }
    }
  }
`;

const mainOrderFields = `
  id
  customerId
  accountNumber
  customerName
  teamworkCustomerId
  regionCode
  type
  changeServiceId
  state
  status
  statusDescription
  createdAt
`;

const inboundApiOrdersFields = `
  id
  apiOrderId
  status
  salesforceOrderId
  orderType
  createdAt
  updatedAt
`;

export interface ServiceOrderSummary {
  id: string;
  customerId: number;
  accountNumber: string;
  customerName: string;
  teamworkCustomerId: number;
  regionCode: string;
  type: string;
  changeServiceId: string;
  state: string;
  status: string;
  statusDescription: string;
  createdAt: string;
}

export interface ServiceOrderSearchResults {
  totalCount: number;
  orders: ServiceOrderSummary[];
}

export const ServiceOrdersQuery = gql`
  query serviceOrders($input: ServiceOrderFilter!) {
    serviceOrders(input: $input) {
      totalCount
      orders {
        ${mainOrderFields}
      }
    }
  }
`;

const getServiceOrderQuery = gql`
  query getServiceOrder($orderId: ID!) {
    getServiceOrder(orderId: $orderId) {
      ${mainOrderFields}
      customer {
        id
        name
        displayName
        regionCode
        salesforceId

        users {
          id
          name
          email
        }

        parent {
          id
          name
          displayName

          users {
            id
            name
            email
          }
        }
      }
      lineItems {
        code
        description
        quantity
      }
      configuration {
        key
        value
      }
      hardwareShippingAddress {
        line1
        line2
        city
        stateName
        stateCode
        postalCode
        countryCode
      }
      services {
        ${mainServiceFields}
        milestones {
          id
          state
          tasks {
            ${taskFields}
          }
        }
      }
      inboundApiOrders {
        ${inboundApiOrdersFields}
      }
    }
  }
`;

interface ServiceTaskByKeyInput {
  serviceId: string;
  taskKey: string;
  state: string;
  status: string;
  statusDescription: string;
  data?: any; // key values
  lastError?: any;
}

// Note: this isn't a complete definition
export interface ServiceTask {
  id: string;
  key: string;
  state: string;
  status: string;
  statusDescription: string;
}

const updateServiceTaskByKeyMutation = gql`
mutation updateServiceTaskByKey($input: ServiceTaskByKeyInput!) {
  updateServiceTaskByKey(input: $input) {
    service {
      id
      state
    }
    milestone {
      id
      state
    }
    task {
      id
      state
      status
      statusDescription
      updatedAt
    }
  }
}
`;

const provisionSipServiceResponseFields = `
  id
  customer {
    id
    pbxNumber
    pbxInfo {
      existsInMeta
      pbxNumber
    }
  }
  type
  state
  status
  statusDescription

  services {
    ${mainServiceFields}
  }

  milestones {
    ${milestoneFields}
  }
`;

const provisionServiceResponseFields = `
  id
  customer {
    id
    pbxNumber
    pbxInfo {
      existsInMeta
      pbxNumber
    }
  }
  type
  state
  status
  statusDescription

  services {
    ${mainServiceFields}
  }

  milestones {
    ${milestoneFields}
  }
`;

const provisionServiceMutation = gql`
mutation provisionService($input: ProvisionServiceInput!) {
  provisionService(input: $input) {
    ${provisionServiceResponseFields}
  }
}
`;

export interface SubAccountServiceSummary {
  id: number;
  name: string;
}

export interface AccountServiceSummary {
  id: number;
  name: string;
  services: [SubAccountServiceSummary];
}

export interface SubCustomersWithServicesForCustomerResponse {
  account: AccountServiceSummary;
  subAccounts: AccountServiceSummary[];
}

export const SubCustomersWithServicesForCustomerQuery = gql`
  query subCustomersWithServicesForCustomer($input: SubCustomersWithServicesForCustomerInput!) {
    subCustomersWithServicesForCustomer(input: $input) {
      account {
        id
        name
        services {
          id
          name
        }
      }
      subAccounts {
        id
        name
        services {
          id
          name
      }
    }
  }
  }
`;

export const bridgeForZoomFields = `
  id
  clientId
  authUrl
  redirectUrl
  stateToken
  allocatedProduct
  tenant {
    id
    adminEmail
    zoomAccountId
    zoomAccountNumber
    displayName
    timezone
    isAuthorized
    tokenRefreshFailed
    firstName
    lastName
  }
  emailAuthStatus {
    customerId
    sentAt
    adminEmail
    status
    actionAt
    token
    expiresAt
  }
`;


export const bridgeForZoomSettingsQuery = gql`
  query bridgeForZoomSettings($serviceId: ID!) {
    bridgeForZoomSettings(serviceId: $serviceId) {
      ${bridgeForZoomFields}
    }
  }
`;


export interface CurrentCustomerOptions {
  includeNonActiveServices?: boolean;
}

interface ServiceMilestoneTask {
  key: string;
  state: string;
  source?: string;
  actor?: string;
}

interface ServiceWithMilestones extends Service {
  milestones?: { tasks: ServiceMilestoneTask[] }[];
}

@Injectable()
export class ServicesService {
  private graphApi = inject(GraphApiService);
  private store = inject<Store<fromRoot.State>>(Store);


  currentCustomer(options: CurrentCustomerOptions = {}): Observable<Customer> {
    // either grab this from the store, or the activated route
    // for now, we will assume the store, but if we normalize on all routes
    // that are under a customer to have a :customerId, we should be good
    return this.store.select(fromRoot.getCurrentCustomer)
      .pipe(
        filter((cust): cust is Customer => !!cust),
        switchMap((cust) => this.customerById(cust.id, 'cache-first', options)),
      )
  }

  currentServiceId(route: ActivatedRoute): Observable<string> {
    return route.params
      .pipe(
        map(params => params['serviceId']),
        filter(serviceId => !!serviceId)
      );
  }

  currentService(route: ActivatedRoute): Observable<Service> {
    return this.currentServiceId(route)
      .pipe(
        switchMap((serviceId: string) => this.pollService(serviceId))
      )
  }

  // currently duplicated from partnerAdminService... probably move to here
  customerById(customerId: number, fetchPolicy: FetchPolicy = 'cache-first', options: CurrentCustomerOptions = {}) {
    const { includeNonActiveServices } = options;
    return this.graphApi.watchQuery<Customer>({
      query: customerByIdQuery,
      variables: { id: customerId },
      fetchPolicy
    }).pipe(
      map((cust) => {
        if (!cust) {
          return cust;
        }
        const allPlatformServices = cust.platformServices || [];
        const platformServices = includeNonActiveServices ? allPlatformServices : allPlatformServices.filter(s => s.status === 'active');
        return {
          ...cust,
          platformServices: normalizePlatformServices(platformServices)
        }
      }),
    );
  }

  getService(serviceId: string) {
    return this.graphApi.watchQuery<Service>({
      query: getServiceQuery,
      variables: { serviceId },
      fetchPolicy: 'cache-and-network'
    });
  }

  pollService(serviceId: string) {
    return this.graphApi.pollQuery<Service>({
      query: getServiceQuery,
      variables: { serviceId }
    });
  }

  getServiceOrder(orderId: string) {
    return this.graphApi.watchQuery<ServiceOrderDetails>({
      query: getServiceOrderQuery,
      variables: { orderId },
      fetchPolicy: 'cache-and-network'
    })
  }

  updateOrderQuery(orderId: string, data: any) {
    const { getServiceOrder } = this.graphApi.readQuery<{ getServiceOrder: ServiceOrderDetails }>({
      query: getServiceOrderQuery,
      variables: { orderId }
    });

    this.graphApi.writeQuery({
      query: getServiceOrderQuery,
      variables: { orderId },
      data: {
        getServiceOrder: {
          ...getServiceOrder,
          ...data
        }
      }
    })
  }

  updateServiceConfiguration(serviceId: string, configuration: any) {
    return this.graphApi.mutate({
      mutation: updateServiceConfigurationMutation,
      variables: {
        input: {
          serviceId,
          configuration
        }
      }
    })
  }

  provisionService(serviceId: string, configuration: Property[]) {
    return this.graphApi.mutate({
      mutation: provisionServiceMutation,
      variables: {
        input: {
          serviceId,
          configuration
        }
      }
    })
  }


  updateServiceTaskByKey(input: ServiceTaskByKeyInput) {
    return this.graphApi.mutate({
      mutation: updateServiceTaskByKeyMutation,
      variables: {
        input
      }
    })
  }

  bridgeForZoomSettings(serviceId: string) {
    return this.graphApi.watchQuery<BridgeForZoomSettings>({
      query: bridgeForZoomSettingsQuery,
      variables: { serviceId }
    });
  }

  findCustomerService(customer: Customer, serviceKey: string): Service | null {
    if (!customer.platformServices) {
      return null;
    }

    return (customer.platformServices.find(s => s.type === serviceKey) as unknown as Service | undefined) ?? null;
  }

  hasService(customer: Customer, serviceKey: string): boolean {
    if (!customer) {
      return false;
    }

    if (customer.mainServiceKey === serviceKey) {
      return true;
    }

    return !!customer.platformServices && !!customer.platformServices.find(s => s.type === serviceKey);
  }

  hasBlueService(customer: Customer): boolean {
    if (!customer || !customer.platformServices) {
      return false;
    }

    return !!customer.platformServices
      // .filter((s: PlatformService) => s.status === 'active')
      .find((s: PlatformService) => isBlueServiceType(s.type));
  }

  hasGreenService(customer: Customer): boolean {
    if (!customer) {
      return false;
    }

    if (isGreenServiceType(customer.mainServiceKey)) {
      return true;
    }

    return !!customer.platformServices && !!customer.platformServices.find(s => isGreenServiceType(s.type))
  }

  findCustomerMainService(customer: Customer): Service | null {
    return this.findCustomerService(customer, customer.mainServiceKey ?? '')
  }

  serviceMenus(customer: Customer) {
    const nonMainServiceMenus = (customer: Customer) => {
      return (customer.platformServices || [])
        .filter(service => service.type !== customer.mainServiceKey)
        .map(service => ({ serviceKey: service.type, service }))
    };

    return [
      { serviceKey: customer.mainServiceKey, service: this.findCustomerMainService(customer) },
      ...nonMainServiceMenus(customer)
    ]
  }

  // Originally we were doing one menu per service, but now we are consolidating
  // some of the menus into one (for example blue-sip and nuso-sip)
  // We want to loop here to maintain the order of the services due to serviceType.position
  platformServiceMenus(customer: Customer, currentService: PlatformService | null, rootPath: string) {
    const consolidatedMappings: Record<string, string> = {
      'blue-sip': 'blue',
      //'ipfax': 'blue', // I don't think we need to show blue menu if just have fax?
      'nuso-sip': 'sip',
      'teams-domain': 'authorization',
      'zoom-domain': 'authorization',
      // Phone and CC instances share one Zoom tenant, so they get one menu/dashboard
      'bridge-zoom': 'bridge-zoom',
    }
    const currentConsolidatedKey = (currentService && consolidatedMappings[currentService.type] || rootPath);
    const hasConsolidatedTypes: string[] = [];

    return (customer.platformServices || [])
      // Below is a hack for teams-sip and teams-pc to display the dashboard even if the service is provisioning or pending
      .filter(service => {
        if (['teams-sip', 'teams-pc', 'teams-domain', 'zoom-domain'].includes(service.type)) return true;
        if (['active', 'setup_required', 'setup_failed'].includes(service.status)) return true;
        return false;
      })
      .filter(service => service.typeFlags.indexOf('hide-from-menu') === -1)
      .map(service => ({ service, consolidatedKey: consolidatedMappings[service.type] }))
      .filter(({ service, consolidatedKey }) => {
        if (consolidatedKey) {
          if (hasConsolidatedTypes.includes(consolidatedKey)) {
            return false;
          }
          hasConsolidatedTypes.push(consolidatedKey);
        }
        return true;
      })
      .map(({ service, consolidatedKey }) => ({
        menuKey: consolidatedKey || service.type,
        service,
        isCurrent: service.id === currentService?.id || (currentConsolidatedKey && currentConsolidatedKey === consolidatedKey)
      }))
  }

  // The customer's menu-visible bridge-zoom instances (Phone and/or CC). Status and
  // hide-from-menu filters match platformServiceMenus so the consolidated menu and the
  // dashboard agree.
  customerBridgeZoomServices(customer: Customer): PlatformService[] {
    return (customer.platformServices || [])
      .filter(service => service.type === 'bridge-zoom')
      .filter(service => ['active', 'setup_required', 'setup_failed'].includes(service.status))
      .filter(service => service.typeFlags.indexOf('hide-from-menu') === -1);
  }

  getPortalRootPath(service: PlatformService) {
    if (service.type === 'blue-sip') {
      return '/blue';
    }

    if (service.type === 'nuso-sip') {
      return '/sip';
    }

    return service.rootPath;
  }

  getPortalRedirectPathForMainService(allServices: PlatformService[]): string | null {
    if (!allServices || allServices.length === 0) {
      return null;
    }

    // Just going to grab the first one.
    // They should be in order of priority.
    // note: if it is blue-sip or nuso-sip, we want to see how many other trunks they have
    // and if just one, redirect straight to that service, otherwise redirect to the /blue or /sip page
    const mainPlatformService: PlatformService | undefined = allServices.find(s => !s.typeFlags.includes('hide-from-menu'));
    if (!mainPlatformService) {
      // The customer has services, but none of these are visible in the UI, so just redirect to the numbers inventory page
      return '/numbers/inventory';
    }

    if (['blue-sip', 'nuso-sip'].includes(mainPlatformService.type)) {
      const sipTrunkServices = allServices.filter(service => service.type === mainPlatformService.type);
      if (sipTrunkServices.length === 1) {
        return mainPlatformService.type === 'nuso-sip' ? `${mainPlatformService.rootPath}/dashboard` : mainPlatformService.rootPath;
      } else {
        return this.getPortalRootPath(mainPlatformService);
      }
    }
    else {
      return this.getPortalRootPath(mainPlatformService);
    }
  }

  currentServiceSubPathSegments(sourceService: PlatformService): string[] {
    const fullPath = window.location.pathname;
    const currentServicePath = sourceService.rootPath;

    const remainderPath = fullPath.startsWith(currentServicePath)
      ? fullPath.slice(currentServicePath.length)
      : fullPath;

    return remainderPath.split('/').filter(s => !!s);
  }

  subCustomersWithServicesForCustomer(customerId: number) {
    return this.graphApi.watchQuery<SubCustomersWithServicesForCustomerResponse>({
      query: SubCustomersWithServicesForCustomerQuery,
      variables: {
        input: {
          customerId
        }
      }
    });
  }

  findMainServiceForOrder(order: { services: Service[]; type: string }) {
    return order.services.find(s => s.type === order.type);
  }

  findTask(service: ServiceWithMilestones, taskKey: string) {
    return _.chain(service.milestones || [])
      .map(m => m.tasks)
      .flatten()
      .find((t) => t.key === taskKey)
      .value()
  }

  findFirstIncompleteTask(service: ServiceWithMilestones) {
    return _.chain(service.milestones || [])
      .map(m => m.tasks)
      .flatten()
      .find((t) => t.state !== 'COMPLETE')
      .value();
  }

  allTasks(service: ServiceWithMilestones) {
    return _.chain(service.milestones || [])
      .map(m => m.tasks)
      .flatten()
      .value();
  }

  managePortalUserTasks(service: ServiceWithMilestones) {
    return this.allTasks(service).filter(t => t.source === 'MANAGE_PORTAL' && t.actor === 'USER');
  }

  customerHasActiveFeature(customer: Customer, featureKey: string) {
    return !!(customer.features || []).find(s => s.featureKey === featureKey && s.status === 'active');
  }

}
